import React, { useRef, useEffect, useState, ReactNode } from "react";

export interface TooltipProps {
  children: ReactNode;
  content: string;
  className?: string;
  disabled?: boolean;
  position?: "right" | "top" | "bottom" | "left";
  offset?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className = "",
  disabled = false,
  position = "right",
  offset = 8
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Calculate tooltip position based on container element
  useEffect(() => {
    const updateTooltipPosition = () => {
      if (containerRef.current && isVisible) {
        const rect = containerRef.current.getBoundingClientRect();
        let top = 0;
        let left = 0;

        switch (position) {
          case "right":
            top = rect.top + rect.height / 2;
            left = rect.right + offset;
            break;
          case "left":
            top = rect.top + rect.height / 2;
            left = rect.left - offset;
            break;
          case "top":
            top = rect.top - offset;
            left = rect.left + rect.width / 2;
            break;
          case "bottom":
            top = rect.bottom + offset;
            left = rect.left + rect.width / 2;
            break;
        }

        setTooltipPosition({ top, left });
      }
    };

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);
    window.addEventListener('scroll', updateTooltipPosition);
    
    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
      window.removeEventListener('scroll', updateTooltipPosition);
    };
  }, [isVisible, position, offset]);

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const getTransformOrigin = () => {
    switch (position) {
      case "right":
        return "translateY(-50%)";
      case "left":
        return "translateY(-50%) translateX(-100%)";
      case "top":
        return "translateX(-50%) translateY(-100%)";
      case "bottom":
        return "translateX(-50%)";
      default:
        return "translateY(-50%)";
    }
  };

  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {children}
      </div>

      {/* Tooltip Portal */}
      {isVisible && (
        <div
          className={`
            pointer-events-none
            fixed z-[9999]
            opacity-100
            transition-opacity duration-200
            whitespace-nowrap
            bg-black/90 dark:bg-white/90
            text-white dark:text-black
            text-xs font-semibold
            rounded px-3 py-1
            shadow-lg
            select-none
            flex items-center
            ${className}
          `}
          style={{
            minWidth: 80,
            maxWidth: 220,
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            transform: getTransformOrigin()
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};
